import { Typography, useTheme } from '@mui/material';

// Components
import { StyledWhiteBox } from 'src/features/PortDetail/config';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import Table from 'src/components/Table';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';

// Utilities
import { IPortTerminal } from 'src/utils/typings/response';

interface IProps {
  listTerminal: Array<IPortTerminal>;
}

const TerminalTable = ({ listTerminal }: IProps) => {
  const theme = useTheme();

  const configColumn: ConfigCell<IPortTerminal>[] = [
    {
      id: 'name',
      disablePadding: true,
      disableSort: true,
      label: 'Name',
      styledCell: (row: IPortTerminal) => ({
        ...styleForEachCell(row),
        borderRight: row.is_total ? '0px' : `1px solid ${theme.palette.divider}`,
      }),
      cell: (row: IPortTerminal) => {
        return (
          <Typography
            variant={row.is_total ? 'subtitle2' : 'body2'}
            color={row.is_total ? 'text.primary' : 'text.secondary'}
          >
            {renderTextOrDefault(row?.name)} <TooltipFormLabel title={renderTextOrDefault(row?.operator) as string} />
          </Typography>
        );
      },
    },
    {
      id: 'portcalls',
      disablePadding: true,
      disableSort: true,
      label: 'Portcalls',
      styledCell: (row: IPortTerminal) => styleForEachCell(row),
      cell: (row: IPortTerminal) => {
        return (
          <Typography
            variant={row.is_total ? 'subtitle2' : 'body2'}
            color={row.is_total ? 'text.primary' : 'text.secondary'}
          >
            {renderNumberOrDefault(row?.portcalls)}
          </Typography>
        );
      },
    },
    {
      id: 'throughput',
      disablePadding: true,
      disableSort: true,
      label: 'Throughput',
      styledCell: (row: IPortTerminal) => styleForEachCell(row),
      cell: (row: IPortTerminal) => {
        return (
          <Typography
            variant={row.is_total ? 'subtitle2' : 'body2'}
            color={row.is_total ? 'text.primary' : 'text.secondary'}
          >
            {renderNumberOrDefault(row?.throughput)}
          </Typography>
        );
      },
    },
    {
      id: 'turnaround',
      disablePadding: true,
      disableSort: true,
      label: 'Turnaround',
      styledCell: (row: IPortTerminal) => styleForEachCell(row),
      cell: (row: IPortTerminal) => {
        return (
          <Typography
            variant={row.is_total ? 'subtitle2' : 'body2'}
            color={row.is_total ? 'text.primary' : 'text.secondary'}
          >
            {renderNumberOrDefault(row?.turnaround)}
          </Typography>
        );
      },
    },
    {
      id: 'dwell_imports',
      disablePadding: true,
      disableSort: true,
      label: 'Dwell Imports',
      styledCell: (row: IPortTerminal) => styleForEachCell(row),
      cell: (row: IPortTerminal) => {
        return (
          <Typography
            variant={row.is_total ? 'subtitle2' : 'body2'}
            color={row.is_total ? 'text.primary' : 'text.secondary'}
          >
            {renderNumberOrDefault(row?.dwell_imports)}
          </Typography>
        );
      },
    },
    {
      id: 'dwell_exports',
      disablePadding: true,
      disableSort: true,
      label: 'Dwell Exports',
      styledCell: (row: IPortTerminal) => styleForEachCell(row),
      cell: (row: IPortTerminal) => {
        return (
          <Typography
            variant={row.is_total ? 'subtitle2' : 'body2'}
            color={row.is_total ? 'text.primary' : 'text.secondary'}
          >
            {renderNumberOrDefault(row?.dwell_exports)}
          </Typography>
        );
      },
    },
  ];

  const styleForEachCell = (row: IPortTerminal) => {
    if (row.is_total) {
      return {
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: '#F4F7FE',
      };
    }
    return {
      borderTop: `0px dashed ${theme.palette.divider}`,
      backgroundColor: '',
    };
  };

  const styleForEachRow = (row: IPortTerminal) => {
    if (row.is_total) {
      return {
        position: 'sticky',
        bottom: '0',
        background: 'white',
      };
    }
    return {
      position: 'unset',
      bottom: '0',
      background: 'white',
    };
  };

  return (
    <StyledWhiteBox display="flex" flexDirection="column" gap={2}>
      <Typography variant="h5" align="left">
        Terminals <TooltipFormLabel title="Statistics for the last 30 days" />
      </Typography>
      <Table
        data={listTerminal}
        configColumn={configColumn}
        dataKey="name"
        isPagination={false}
        maxRow={5}
        totalRecords={getArrayDefault(listTerminal).length}
        styledRow={(row: IPortTerminal) => styleForEachRow(row)}
        sxTableContainer={{
          border: `1px solid ${theme.palette.divider}`,
        }}
      />
    </StyledWhiteBox>
  );
};

export default TerminalTable;
